'use client';

import {
  AccordionCloseIconSlot,
  AccordionContentSlot,
  AccordionItemSlot,
  AccordionOpenIconSlot,
  AccordionTriggerDescriptionSlot,
  AccordionTriggerLabelSlot,
  AccordionTriggerSlot,
  AccordionTriggerTrailingContent,
} from '@magicschool/spellbook/components/Accordion/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as AccordionPrimitive from 'radix-ui/accordion';
import { useDirection } from 'radix-ui/direction';

interface AccordionSingleProps extends Omit<AccordionPrimitive.AccordionSingleProps, 'type'> {
  type: 'single';
}

interface AccordionMultipleProps extends Omit<AccordionPrimitive.AccordionMultipleProps, 'type'> {
  type?: 'multiple';
}

const accordionVariants = cva('w-full', {
  variants: {
    variant: {
      stacking: '',
      card: 'flex flex-col gap-2',
    },
  },
});

export type AccordionProps = (AccordionSingleProps | AccordionMultipleProps) &
  VariantProps<typeof accordionVariants> & {
    /** The position of the open/close icon. */
    iconPosition?: 'start' | 'end';
    /** Whether items can be collapsed. When type="single", defaults to true. */
    collapsible?: boolean;
    /**
     * When true, the accordion item and its content expand to fill available vertical space
     * using flex column layout. Use on pages where the accordion must fill a fixed-height
     * container rather than sizing to its content.
     */
    fillHeight?: boolean;
  };

function AccordionBase({
  children,
  className,
  iconPosition = 'start',
  type = 'multiple',
  variant,
  collapsible,
  fillHeight,
  ...rest
}: AccordionProps) {
  const { slots } = useSlots(children, {
    items: { Component: AccordionItemSlot, props: { iconPosition, variant, fillHeight } },
  });

  const dir = useDirection();

  // When type is 'single', default collapsible to true if not explicitly provided
  const collapsibleValue = type === 'single' && collapsible === undefined ? true : collapsible;

  const typeguardedProps =
    type === 'multiple'
      ? ({ type: 'multiple', ...rest } as AccordionPrimitive.AccordionMultipleProps)
      : ({ type: 'single', collapsible: collapsibleValue, ...rest } as AccordionPrimitive.AccordionSingleProps);

  return (
    <AccordionPrimitive.Root
      className={cn(accordionVariants({ variant }), className)}
      data-spellbook-name="accordion"
      dir={dir}
      {...typeguardedProps}
    >
      {slots.items}
    </AccordionPrimitive.Root>
  );
}

export const Accordion = Object.assign(AccordionBase, {
  Item: AccordionItemSlot,
  Trigger: AccordionTriggerSlot,
  TriggerLabel: AccordionTriggerLabelSlot,
  TriggerDescription: AccordionTriggerDescriptionSlot,
  Content: AccordionContentSlot,
  OpenIcon: AccordionOpenIconSlot,
  CloseIcon: AccordionCloseIconSlot,
  TriggerTrailingContent: AccordionTriggerTrailingContent,
});
